import React, { useCallback, useId, useMemo, useRef, useState } from "react";
import {
  Activity,
  BarChart2,
  CircleDollarSign,
  Clock3,
  Crown,
  Eye,
  Flame,
  Heart,
  Landmark,
  Ticket,
  Trophy,
  Users,
  Wallet,
  Zap,
} from "lucide-react";
import {
  AboutHouseSection,
  BrandNotesSection,
  FixturesScheduleSection,
  OperatorsSection,
  RecentStakesSection,
  StakesLimitsSection,
} from "./DetailSections";
import { fmtCash, fmtCompact, fmtFull } from "../../utils/betDetailHelpers";
import {
  activityBadge,
  buildActivityRangeStats,
  buildActivityTimeline,
  buildAudienceFunnel,
  buildBettorInsights,
  buildCommunityStats,
  buildHourHistogram,
  buildLeagueBoardMix,
  buildPoolHealth,
  buildStakeDistribution,
  CHART_MODES,
  compactCash,
  dayKeyLabel,
  RANGES,
} from "../../utils/betDetailAnalytics";

const VIEW_W = 640;
const VIEW_H = 220;
const PAD_TOP = 14;
const PAD_BOTTOM = 22;

const clamp01 = (v) => Math.min(1, Math.max(0, v));

/* ── Sparkline (cumulative volume) ── */

const VolumeSparkline = ({ timeline }) => {
  const gradId = useId();
  const W = 200;
  const H = 48;

  const { path, area } = useMemo(() => {
    const values = timeline.map((p) => p.cumulativeVolume);
    if (values.length < 2 || values.every((v) => v === 0)) {
      return { path: "", area: "" };
    }
    const lo = 0;
    const hi = Math.max(...values, 1);
    const span = Math.max(hi - lo, 1e-9);
    const toX = (i) => (i / (values.length - 1)) * W;
    const toY = (v) => 4 + (1 - (v - lo) / span) * (H - 8);

    let d = `M ${toX(0).toFixed(1)} ${toY(values[0]).toFixed(1)}`;
    for (let i = 1; i < values.length; i += 1) {
      d += ` L ${toX(i).toFixed(1)} ${toY(values[i]).toFixed(1)}`;
    }
    return { path: d, area: `${d} L ${W} ${H} L 0 ${H} Z` };
  }, [timeline]);

  if (!path) return null;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="bd-an-sparkline" preserveAspectRatio="none" aria-hidden>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#64b5f6" stopOpacity="0.28" />
          <stop offset="100%" stopColor="#1e88e5" stopOpacity="0.01" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradId})`} />
      <path d={path} fill="none" stroke="#64b5f6" strokeWidth="1.8" vectorEffect="non-scaling-stroke" />
    </svg>
  );
};

/* ── Activity chart ── */

const ActivityChart = ({ timeline, mode }) => {
  const gradId = useId();
  const surfaceRef = useRef(null);
  const [hoverIndex, setHoverIndex] = useState(null);

  const n = timeline.length;
  const hasActivity = timeline.some(
    (p) => p.placedStake !== 0 || p.placedCount !== 0 || p.winningsPaid !== 0
  );

  const { yMin, yMax } = useMemo(() => {
    let lo = 0;
    let hi = 0;
    for (const p of timeline) {
      if (mode === "cumulative") {
        hi = Math.max(hi, p.cumulativeVolume);
      } else if (mode === "volume") {
        hi = Math.max(hi, p.placedStake);
      } else if (mode === "tickets") {
        hi = Math.max(hi, p.placedCount);
      } else {
        hi = Math.max(hi, p.winningsPaid);
      }
    }
    if (hi === 0) hi = 1;
    const pad = hi * 0.08;
    return { yMin: 0, yMax: hi + pad };
  }, [timeline, mode]);

  const innerH = VIEW_H - PAD_TOP - PAD_BOTTOM;
  const span = Math.max(yMax - yMin, 1e-9);
  const toY = useCallback(
    (v) => PAD_TOP + (1 - (v - yMin) / span) * innerH,
    [yMin, span, innerH]
  );
  const zeroY = toY(0);
  const slotW = n > 0 ? VIEW_W / n : VIEW_W;
  const centerX = useCallback((i) => (i + 0.5) * slotW, [slotW]);

  const { linePath, areaPath } = useMemo(() => {
    if (mode !== "cumulative" || !n) return { linePath: "", areaPath: "" };
    const pts =
      n === 1
        ? [
            { x: 0, y: toY(timeline[0].cumulativeVolume) },
            { x: VIEW_W, y: toY(timeline[0].cumulativeVolume) },
          ]
        : timeline.map((p, i) => ({ x: centerX(i), y: toY(p.cumulativeVolume) }));
    let d = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;
    for (let i = 1; i < pts.length; i += 1) {
      d += ` L ${pts[i].x.toFixed(1)} ${pts[i].y.toFixed(1)}`;
    }
    const base = VIEW_H - PAD_BOTTOM;
    return {
      linePath: d,
      areaPath: `${d} L ${pts[pts.length - 1].x.toFixed(1)} ${base.toFixed(1)} L ${pts[0].x.toFixed(1)} ${base.toFixed(1)} Z`,
    };
  }, [mode, n, timeline, centerX, toY]);

  const handleMove = (event) => {
    if (!n || !surfaceRef.current) return;
    const rect = surfaceRef.current.getBoundingClientRect();
    const frac = clamp01((event.clientX - rect.left) / rect.width);
    setHoverIndex(Math.min(n - 1, Math.floor(frac * n)));
  };

  const hover = hoverIndex != null ? timeline[hoverIndex] : null;
  const hoverXFrac = hoverIndex != null ? (hoverIndex + 0.5) / n : 0;

  const axisIndices = useMemo(() => {
    if (n <= 1) return n === 1 ? [0] : [];
    if (n <= 8) return timeline.map((_, i) => i);
    const out = [];
    for (let k = 0; k < 5; k += 1) out.push(Math.round((k / 4) * (n - 1)));
    return [...new Set(out)];
  }, [n, timeline]);

  const formatAxis = (v) => (mode === "tickets" ? String(Math.round(v)) : compactCash(v));

  if (!n || !hasActivity) {
    return (
      <div className="bd-an-chart-empty">
        <Activity size={15} />
        <span>No activity in this window yet — charts draw as players start betting.</span>
      </div>
    );
  }

  const barW = Math.min(slotW * 0.56, 30);

  return (
    <div
      ref={surfaceRef}
      className="bd-an-chart-surface"
      onPointerMove={handleMove}
      onPointerLeave={() => setHoverIndex(null)}
    >
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="none"
        className="bd-an-chart-svg"
        role="img"
        aria-label="House activity chart"
      >
        <defs>
          <linearGradient id={`${gradId}-flat`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#64b5f6" stopOpacity="0.28" />
            <stop offset="100%" stopColor="#1e88e5" stopOpacity="0.01" />
          </linearGradient>
        </defs>

        {[yMax, yMax / 2, yMin].map((level, i) => (
          <line
            key={`${level}-${i}`}
            x1={0}
            x2={VIEW_W}
            y1={toY(level)}
            y2={toY(level)}
            className="bd-an-grid"
            vectorEffect="non-scaling-stroke"
          />
        ))}

        {mode === "cumulative" ? (
          <>
            <path d={areaPath} fill={`url(#${gradId}-flat)`} />
            <path
              d={linePath}
              className="bd-an-line bd-an-line-flat"
              fill="none"
              vectorEffect="non-scaling-stroke"
            />
          </>
        ) : null}

        {mode === "volume" || mode === "tickets" || mode === "payouts"
          ? timeline.map((p, i) => {
              const value =
                mode === "tickets"
                  ? p.placedCount
                  : mode === "payouts"
                  ? p.winningsPaid
                  : p.placedStake;
              if (value === 0) return null;
              const y = toY(value);
              return (
                <rect
                  key={p.date}
                  x={centerX(i) - barW / 2}
                  y={y}
                  width={barW}
                  height={Math.max(zeroY - y, 1.5)}
                  rx={2.5}
                  className={mode === "payouts" ? "bd-an-bar-up" : "bd-an-bar-flat"}
                  opacity={hoverIndex == null || hoverIndex === i ? 1 : 0.45}
                />
              );
            })
          : null}

        {hover ? (
          <line
            x1={hoverXFrac * VIEW_W}
            x2={hoverXFrac * VIEW_W}
            y1={PAD_TOP}
            y2={VIEW_H - PAD_BOTTOM}
            className="bd-an-crosshair"
            vectorEffect="non-scaling-stroke"
          />
        ) : null}
      </svg>

      <div className="bd-an-yaxis" aria-hidden>
        <span>{formatAxis(yMax)}</span>
        <span>{formatAxis(yMin)}</span>
      </div>

      <div className="bd-an-xaxis" aria-hidden>
        {axisIndices.map((i) => (
          <span key={timeline[i]?.date || i} style={{ left: `${((i + 0.5) / n) * 100}%` }}>
            {dayKeyLabel(timeline[i]?.date)}
          </span>
        ))}
      </div>

      {hover ? (
        <div
          className={`bd-an-tooltip tabular-nums${hoverXFrac > 0.7 ? " bd-an-tooltip-left" : ""}`}
          style={{ left: `${hoverXFrac * 100}%` }}
        >
          <em>{dayKeyLabel(hover.date)}</em>
          {mode === "tickets" ? (
            <>
              <strong>{hover.placedCount}</strong>
              <span>ticket{hover.placedCount === 1 ? "" : "s"} placed</span>
            </>
          ) : mode === "payouts" ? (
            <>
              <strong>{compactCash(hover.winningsPaid)}</strong>
              <span>{hover.settledCount} winner{hover.settledCount === 1 ? "" : "s"} paid</span>
            </>
          ) : (
            <>
              <strong>
                {compactCash(mode === "cumulative" ? hover.cumulativeVolume : hover.placedStake)}
              </strong>
              <span>
                {hover.placedCount} ticket{hover.placedCount === 1 ? "" : "s"}
                {hover.winningsPaid > 0 ? ` · ${compactCash(hover.winningsPaid)} won` : ""}
              </span>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
};

/* ── Sub-panels ── */

const ChipRow = ({ options, value, onChange }) => (
  <div className="bd-an-chips">
    {options.map((opt) => (
      <button
        key={opt.id}
        type="button"
        className={`bd-an-chip ${value === opt.id ? "bd-an-chip-active" : ""}`}
        onClick={() => onChange(opt.id)}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

const KpiStrip = ({ rangeStats, community, poolHealth, pulse }) => {
  const tiles = [
    {
      id: "volume",
      Icon: Wallet,
      label: "Volume",
      value: compactCash(rangeStats.placedStake),
    },
    {
      id: "tickets",
      Icon: Ticket,
      label: "Tickets",
      value: rangeStats.placedCount.toLocaleString(),
    },
    {
      id: "bettors",
      Icon: Users,
      label: "Players",
      value: pulse.uniqueBettors.toLocaleString(),
    },
    {
      id: "avg",
      Icon: BarChart2,
      label: "Avg stake",
      value: fmtCompact(community.avgStake),
    },
    {
      id: "won",
      Icon: Trophy,
      label: "Winnings paid",
      value: compactCash(rangeStats.winningsPaid),
      tone: rangeStats.winningsPaid > 0 ? "bd-an-up" : "",
    },
    {
      id: "pool",
      Icon: Landmark,
      label: "Pool available",
      value: compactCash(poolHealth.available),
      tone: poolHealth.status === "low" ? "bd-an-down" : "",
    },
  ];

  return (
    <div className="bd-an-kpis">
      {tiles.map(({ id, Icon, label, value, tone }) => (
        <article key={id} className="bd-an-kpi">
          <span className="bd-an-kpi-icon">
            <Icon size={12} strokeWidth={2.4} />
          </span>
          <span className="bd-an-kpi-label">{label}</span>
          <strong className={`bd-an-kpi-value tabular-nums ${tone || ""}`}>{value}</strong>
        </article>
      ))}
    </div>
  );
};

const InsightsStrip = ({ insights, community }) => (
  <div className="bd-an-insights">
    <article className="bd-an-insight">
      <span className="bd-an-insight-icon">
        <Flame size={12} />
      </span>
      <div>
        <span className="bd-an-insight-label">Busiest day</span>
        <strong className="tabular-nums">
          {insights.busiestDay ? insights.busiestDay.placedCount : "—"}
        </strong>
        <em>
          {insights.busiestDay
            ? `${dayKeyLabel(insights.busiestDay.date)} · ${compactCash(insights.busiestDay.placedStake)}`
            : "No tickets yet"}
        </em>
      </div>
    </article>
    <article className="bd-an-insight">
      <span className="bd-an-insight-icon">
        <Zap size={12} />
      </span>
      <div>
        <span className="bd-an-insight-label">Peak volume day</span>
        <strong className="tabular-nums">
          {insights.peakVolumeDay ? compactCash(insights.peakVolumeDay.placedStake) : "—"}
        </strong>
        <em>
          {insights.peakVolumeDay ? dayKeyLabel(insights.peakVolumeDay.date) : "No volume yet"}
        </em>
      </div>
    </article>
    <article className="bd-an-insight">
      <span className="bd-an-insight-icon">
        <Crown size={12} />
      </span>
      <div>
        <span className="bd-an-insight-label">Biggest stake</span>
        <strong className="tabular-nums">
          {insights.biggestStake ? compactCash(insights.biggestStake.amount) : "—"}
        </strong>
        <em>
          Median {insights.medianStake > 0 ? compactCash(insights.medianStake) : "—"}
        </em>
      </div>
    </article>
    <article className="bd-an-insight">
      <span className="bd-an-insight-icon">
        <Users size={12} />
      </span>
      <div>
        <span className="bd-an-insight-label">Return players</span>
        <strong className="tabular-nums">
          {insights.repeatRate != null ? `${insights.repeatRate}%` : "—"}
        </strong>
        <em>
          {community.repeatBettors > 0
            ? `${community.repeatBettors} played more than once`
            : `${community.uniqueBettors} unique players`}
        </em>
      </div>
    </article>
  </div>
);

const LeagueIntel = ({ leagues }) => {
  const [view, setView] = useState("bettors");
  const peak = Math.max(
    ...leagues.map((l) => (view === "bettors" ? l.bettorsCount : l.fixtureCount)),
    1
  );

  return (
    <section className="bd-an-card">
      <header className="bd-an-card-head">
        <div>
          <span className="bd-an-kicker">Board intel</span>
          <p className="bd-an-caption">Where players are betting on this house</p>
        </div>
        <div className="bd-an-toggle-row">
          {[
            { id: "bettors", label: "Players" },
            { id: "fixtures", label: "Fixtures" },
          ].map((v) => (
            <button
              key={v.id}
              type="button"
              className={`bd-an-toggle ${view === v.id ? "bd-an-toggle-on" : ""}`}
              onClick={() => setView(v.id)}
            >
              {v.label}
            </button>
          ))}
        </div>
      </header>

      {!leagues.length ? (
        <p className="bd-an-empty-copy">League breakdown appears once fixtures are on the board.</p>
      ) : (
        <ul className="bd-an-league-list">
          {leagues.slice(0, 8).map((row) => {
            const value = view === "bettors" ? row.bettorsCount : row.fixtureCount;
            const width = Math.max((value / peak) * 100, value > 0 ? 4 : 0);
            return (
              <li key={row.key} className="bd-an-league-row">
                {row.logo ? (
                  <img src={row.logo} alt="" className="bd-an-league-logo" />
                ) : (
                  <span className="bd-an-league-logo bd-an-league-logo-blank">
                    <Trophy size={11} />
                  </span>
                )}
                <div className="bd-an-league-copy">
                  <div className="bd-an-league-top">
                    <strong>{row.name}</strong>
                    <em className="tabular-nums">{value.toLocaleString()}</em>
                  </div>
                  <span className="bd-an-league-track" aria-hidden>
                    <span className="bd-an-track-flat" style={{ width: `${width}%` }} />
                  </span>
                  <span className="bd-an-league-hint">
                    {row.live > 0 ? `${row.live} live · ` : ""}
                    {row.upcoming} upcoming · {row.markets} mkts
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

const HourHistogram = ({ histogram }) => {
  const { buckets, peak } = histogram;
  const hasData = buckets.some((b) => b.count > 0);
  if (!hasData) return null;

  const busiest = buckets.reduce((best, b) => (b.count > (best?.count || 0) ? b : best), null);

  return (
    <section className="bd-an-card bd-an-card-compact">
      <header className="bd-an-card-head">
        <div>
          <span className="bd-an-kicker">Peak hours</span>
          <p className="bd-an-caption">
            {busiest
              ? `Most action around ${busiest.hour}:00`
              : "When players place tickets"}
          </p>
        </div>
      </header>
      <div className="bd-an-hour-chart" role="img" aria-label="Hourly activity distribution">
        {buckets.map((b) => (
          <div key={b.hour} className="bd-an-hour-cell">
            <span
              className="bd-an-hour-bar"
              style={{ height: `${Math.max((b.count / peak) * 100, b.count > 0 ? 8 : 0)}%` }}
            />
            <span className="bd-an-hour-label">{b.hour % 6 === 0 ? `${b.hour}` : ""}</span>
          </div>
        ))}
      </div>
    </section>
  );
};

const StakeDistribution = ({ distribution }) => {
  if (!distribution.length) return null;
  const peak = Math.max(...distribution.map((d) => d.count), 1);

  return (
    <section className="bd-an-card bd-an-card-compact">
      <header className="bd-an-card-head">
        <div>
          <span className="bd-an-kicker">Typical stakes</span>
          <p className="bd-an-caption">How players size tickets on this house</p>
        </div>
      </header>
      <ul className="bd-an-dist-list">
        {distribution.map((row) => (
          <li key={row.id} className="bd-an-dist-row">
            <span className="bd-an-dist-label">{row.label}</span>
            <span className="bd-an-dist-track" aria-hidden>
              <span style={{ width: `${Math.max((row.count / peak) * 100, row.count > 0 ? 4 : 0)}%` }} />
            </span>
            <em className="tabular-nums">{row.pct}%</em>
          </li>
        ))}
      </ul>
    </section>
  );
};

const AudienceFunnel = ({ stages }) => (
  <section className="bd-an-card">
    <header className="bd-an-card-head">
      <div>
        <span className="bd-an-kicker">Community</span>
        <p className="bd-an-caption">How players discover and return to this house</p>
      </div>
    </header>
    <div className="bd-an-funnel">
      {stages.map((stage) => (
        <div key={stage.id} className="bd-an-funnel-stage">
          <div className="bd-an-funnel-meta">
            <span className="bd-an-funnel-label">
              {stage.id === "views" ? <Eye size={11} /> : null}
              {stage.id === "followers" ? <Heart size={11} /> : null}
              {stage.id === "bettors" ? <Users size={11} /> : null}
              {stage.id === "repeat" ? <Crown size={11} /> : null}
              {stage.label}
            </span>
            <strong className="tabular-nums">{stage.value.toLocaleString()}</strong>
          </div>
          <div className="bd-an-funnel-track">
            <span className="bd-an-funnel-fill" style={{ width: `${stage.widthPct}%` }} />
          </div>
          <span className="bd-an-funnel-hint">
            {stage.conv != null
              ? `${stage.conv}% of ${stages[stages.indexOf(stage) - 1]?.label.toLowerCase()}`
              : stage.hint || "Top of funnel"}
          </span>
        </div>
      ))}
    </div>
  </section>
);

const PoolHealthPanel = ({ treasury, poolHealth }) => {
  const segments = poolHealth.segments;
  const total = poolHealth.total || 1;

  const pieStyle = {
    background: (() => {
      let cursor = 0;
      const colors = { primary: "var(--bd-primary)", warn: "var(--bd-warning)" };
      const stops = segments.map((seg) => {
        const pct = (seg.value / total) * 100;
        const start = cursor;
        cursor += pct;
        return `${colors[seg.tone] || colors.primary} ${start}% ${cursor}%`;
      });
      if (cursor < 100) stops.push(`rgba(30, 136, 229, 0.12) ${cursor}% 100%`);
      return `conic-gradient(${stops.join(", ")})`;
    })(),
  };

  const statusTone =
    poolHealth.status === "low"
      ? "bd-an-down"
      : poolHealth.status === "busy"
      ? "bd-an-up"
      : "";

  return (
    <section className="bd-an-card">
      <header className="bd-an-card-head">
        <div>
          <span className="bd-an-kicker">Pool health</span>
          <p className="bd-an-caption">Liquidity available for payouts on this house</p>
        </div>
        <span className="bd-an-card-icon">
          <CircleDollarSign size={15} />
        </span>
      </header>

      <div className="bd-an-treasury-hero">
        <div className="bd-an-treasury-pie" style={pieStyle} role="img" aria-label="Pool breakdown">
          <div className="bd-an-treasury-pie-core">
            <span>Total pool</span>
            <strong className="tabular-nums">{fmtCash(treasury.balance || poolHealth.total)}</strong>
          </div>
        </div>
        <div className="bd-an-treasury-stats">
          <div className="bd-an-treasury-stat">
            <span>Status</span>
            <strong className={`tabular-nums ${statusTone}`}>{poolHealth.statusLabel}</strong>
          </div>
          <div className="bd-an-treasury-stat">
            <span>Available</span>
            <strong className="tabular-nums">{poolHealth.availablePct}%</strong>
          </div>
          <div className="bd-an-treasury-stat">
            <span>In play</span>
            <strong className="tabular-nums">{poolHealth.inPlayPct}%</strong>
          </div>
        </div>
      </div>

      <ul className="bd-an-treasury-rows">
        {segments.map((seg) => (
          <li key={seg.key} className={`bd-an-treasury-row bd-an-treasury-row-${seg.tone}`}>
            <span>{seg.label}</span>
            <strong className="tabular-nums">{fmtCash(seg.value)}</strong>
            <em className="tabular-nums">{Math.round((seg.value / total) * 100)}%</em>
          </li>
        ))}
      </ul>

      {poolHealth.status === "low" ? (
        <p className="bd-an-pool-note bd-an-down">
          Pool is near the low-balance threshold — consider stake size before placing large tickets.
        </p>
      ) : null}
    </section>
  );
};

/* ── Main desk ── */

const HouseAnalyticsDesk = ({
  house,
  fixtures,
  pulse,
  treasury,
  recentStakes,
  about,
  operators,
  stakeRows,
}) => {
  const [range, setRange] = useState(30);
  const [chartMode, setChartMode] = useState("cumulative");

  const timeline = useMemo(() => buildActivityTimeline(house, range), [house, range]);
  const rangeStats = useMemo(() => buildActivityRangeStats(timeline), [timeline]);
  const community = useMemo(() => buildCommunityStats(house), [house]);
  const insights = useMemo(
    () => buildBettorInsights(house, rangeStats, community),
    [house, rangeStats, community]
  );
  const leagues = useMemo(() => buildLeagueBoardMix(fixtures), [fixtures]);
  const histogram = useMemo(() => buildHourHistogram(house), [house]);
  const distribution = useMemo(() => buildStakeDistribution(house), [house]);
  const funnelStages = useMemo(() => buildAudienceFunnel(house, pulse), [house, pulse]);
  const poolHealth = useMemo(() => buildPoolHealth(treasury, house), [treasury, house]);

  const badge = activityBadge(rangeStats, range);
  const rangeLabel = RANGES.find((r) => r.id === range)?.label || range;

  return (
    <div className="bd-an-desk">
      <section className="bd-an-hero">
        <div className="bd-an-hero-main">
          <div className="bd-an-hero-top">
            <span className="bd-an-kicker">House activity · {rangeLabel}</span>
            <span className={`bd-an-hero-badge ${badge.tone}`}>{badge.label}</span>
          </div>
          <strong className="bd-an-hero-value tabular-nums">
            {compactCash(rangeStats.placedStake)}
          </strong>
          <span className="bd-an-hero-sub">
            {rangeStats.placedCount.toLocaleString()} ticket
            {rangeStats.placedCount === 1 ? "" : "s"} · {pulse.uniqueBettors} player
            {pulse.uniqueBettors === 1 ? "" : "s"} · {fmtFull(community.totalVolume)} all-time volume
          </span>
          <div className="bd-an-hero-spark">
            <VolumeSparkline timeline={timeline} />
          </div>
        </div>

        <div className="bd-an-hero-tiles">
          <div className="bd-an-hero-tile">
            <Ticket size={12} />
            <span>Tickets</span>
            <strong className="tabular-nums">{rangeStats.placedCount.toLocaleString()}</strong>
          </div>
          <div className="bd-an-hero-tile">
            <Users size={12} />
            <span>Players</span>
            <strong className="tabular-nums">{pulse.uniqueBettors.toLocaleString()}</strong>
          </div>
          <div className="bd-an-hero-tile">
            <Trophy size={12} />
            <span>Won</span>
            <strong className="tabular-nums bd-an-up">{compactCash(rangeStats.winningsPaid)}</strong>
          </div>
          <div className="bd-an-hero-tile">
            <Landmark size={12} />
            <span>Pool</span>
            <strong className="tabular-nums">{compactCash(poolHealth.available)}</strong>
          </div>
        </div>

        <ChipRow options={RANGES} value={range} onChange={setRange} />
      </section>

      <KpiStrip
        rangeStats={rangeStats}
        community={community}
        poolHealth={poolHealth}
        pulse={pulse}
      />

      <div className="bd-an-grid">
        <div className="bd-an-grid-main">
          <div className="bd-an-grid-main-charts">
            <section className="bd-an-card bd-an-card-chart">
              <header className="bd-an-card-head">
                <div>
                  <span className="bd-an-kicker">Activity</span>
                  <p className="bd-an-caption">
                    {chartMode === "payouts"
                      ? "Winnings paid to players each day"
                      : chartMode === "tickets"
                      ? "Tickets placed per day"
                      : chartMode === "volume"
                      ? "New handle placed each day"
                      : "Running total volume on this house"}
                  </p>
                </div>
                <div className="bd-an-toggle-row">
                  {CHART_MODES.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      className={`bd-an-toggle ${chartMode === m.id ? "bd-an-toggle-on" : ""}`}
                      onClick={() => setChartMode(m.id)}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </header>
              <ActivityChart timeline={timeline} mode={chartMode} />
            </section>

            <InsightsStrip insights={insights} community={community} />

            <div className="bd-an-duo">
              <LeagueIntel leagues={leagues} />
              <div className="bd-an-duo-stack">
                <HourHistogram histogram={histogram} />
                <StakeDistribution distribution={distribution} />
              </div>
            </div>

            <div className="bd-an-grid-side-panels">
              <PoolHealthPanel treasury={treasury} poolHealth={poolHealth} />
              <AudienceFunnel stages={funnelStages} />
            </div>
          </div>

          <div className="bd-an-secondary">
            <div className="bd-an-secondary-main">
              <RecentStakesSection stakes={recentStakes} />
              <AboutHouseSection about={about} />
              <BrandNotesSection brandStory={about.brandStory} />
            </div>
            <div className="bd-an-secondary-side">
              <StakesLimitsSection rows={stakeRows} />
              <OperatorsSection operators={operators} />
            </div>
          </div>
        </div>

        <div className="bd-an-grid-side">
          <FixturesScheduleSection fixtures={fixtures} isPaused={pulse.isPaused} />
        </div>
      </div>
    </div>
  );
};

export default HouseAnalyticsDesk;
